
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { CheckCircle, ChevronRight, ChevronLeft, Upload, Calendar, AlertCircle } from "lucide-react";
import PageTransition from "@/components/PageTransition";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";

// Our persona options with their specific onboarding flows
const personas = [
  {
    id: "household",
    title: "Household",
    description: "Track groceries, medicines, and home goods",
    dataOptions: ["manual", "csv"],
    icon: "🏠"
  },
  {
    id: "restaurant",
    title: "Restaurant",
    description: "Manage food inventory and supplies",
    dataOptions: ["manual", "barcode", "csv"],
    icon: "🍽️"
  },
  {
    id: "retail",
    title: "Retail",
    description: "Track store inventory and products",
    dataOptions: ["manual", "pos", "csv"],
    icon: "🛒"
  },
  {
    id: "healthcare",
    title: "Healthcare",
    description: "Monitor medical supplies and medications",
    dataOptions: ["manual", "batch", "csv"],
    icon: "🏥"
  }
];

const dataSourceLabels: Record<string, string> = {
  manual: "Manual Entry",
  csv: "CSV Upload",
  barcode: "Barcode Scan",
  pos: "POS Integration",
  batch: "Batch Upload"
};

const Onboard = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [step, setStep] = useState(1);
  const [selectedPersona, setSelectedPersona] = useState("");
  const [selectedDataSource, setSelectedDataSource] = useState("");
  const [alertWindow, setAlertWindow] = useState(7);
  const [complianceChecked, setComplianceChecked] = useState(false);
  const [fileSelected, setFileSelected] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const totalSteps = 3;

  useEffect(() => {
    // Redirect to auth if not logged in
    if (!loading && !user) {
      toast.error("Please sign in to access the onboarding process");
      navigate("/auth");
    }
  }, [user, loading, navigate]);
  
  const handlePersonaSelect = (personaId: string) => {
    setSelectedPersona(personaId);
  };
  
  const handleDataSourceSelect = (source: string) => {
    setSelectedDataSource(source);
    if (source === "csv" || source === "batch") {
      setFileSelected(false);
    }
  };
  
  const handleFileSelect = () => {
    // In a real app, this would handle actual file upload logic
    setFileSelected(true);
    toast.success("File selected successfully");
  };
  
  const handleNext = async () => {
    // Validation for each step
    if (step === 1 && !selectedPersona) {
      toast.error("Please select a role to continue");
      return;
    }
    
    if (step === 2) {
      if (!selectedDataSource) {
        toast.error("Please select a data source to continue");
        return;
      }
      
      if ((selectedDataSource === "csv" || selectedDataSource === "batch") && !fileSelected) {
        toast.error("Please upload a file to continue");
        return;
      }
    }
    
    if (step === 3) {
      if (selectedPersona === "healthcare" && !complianceChecked) {
        toast.error("Please confirm regulatory compliance to continue");
        return;
      }
      
      // Save all onboarding data to user profile
      try {
        setIsSubmitting(true);
        
        if (!user) {
          toast.error("You must be signed in to complete onboarding");
          navigate("/auth");
          return;
        }
        
        const { error } = await supabase
          .from('profiles')
          .update({
            persona: selectedPersona,
            data_source: selectedDataSource,
            alert_window: alertWindow,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id);
          
        if (error) throw error;
        
        toast.success("Onboarding complete!");
        navigate("/dashboard");
        return;
      } catch (error: any) {
        toast.error(error.message || "Error saving preferences");
        console.error("Error saving onboarding data:", error);
      } finally {
        setIsSubmitting(false);
      }
    }
    
    setStep(prevStep => prevStep + 1);
  };
  
  const handleBack = () => {
    if (step === 1) {
      navigate("/");
    } else {
      setStep(prevStep => prevStep - 1);
    }
  };

  const getDataSourceOptions = () => {
    const persona = personas.find(p => p.id === selectedPersona);
    return persona ? persona.dataOptions : [];
  };
  
  const isHealthcare = selectedPersona === "healthcare";
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }
  
  return (
    <PageTransition className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4 py-16">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-card p-8 md:p-10">
        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex justify-between mb-2">
            {[1, 2, 3].map((stepNumber) => (
              <div key={stepNumber} className="flex flex-col items-center">
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                    ${step >= stepNumber ? 'bg-expitrack-primary text-white' : 'bg-gray-100 text-gray-400'}`}
                >
                  {step > stepNumber ? <CheckCircle className="w-5 h-5" /> : stepNumber}
                </div>
                <span className="text-xs mt-1 text-gray-500">
                  {stepNumber === 1 ? 'Role' : stepNumber === 2 ? 'Data' : 'Alerts'}
                </span>
              </div>
            ))}
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div 
              className="bg-expitrack-primary h-2 rounded-full transition-all duration-500"
              style={{ width: `${(step / totalSteps) * 100}%` }}
            ></div>
          </div>
        </div>
      
        {/* Step content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {step === 1 && (
              <div>
                <h2 className="text-2xl font-bold mb-2">Select your role</h2>
                <p className="text-gray-600 mb-6">
                  Choose the option that best describes how you'll use ExpiTrack.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  {personas.map((persona) => (
                    <div 
                      key={persona.id}
                      className={`p-4 rounded-xl border-2 transition-all cursor-pointer
                        ${selectedPersona === persona.id 
                          ? 'border-expitrack-primary bg-expitrack-primary/5' 
                          : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'}`}
                      onClick={() => handlePersonaSelect(persona.id)}
                    >
                      <div className="flex items-start">
                        <div className="text-2xl mr-3">{persona.icon}</div>
                        <div>
                          <h3 className="font-semibold">{persona.title}</h3>
                          <p className="text-sm text-gray-600">{persona.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {step === 2 && (
              <div>
                <h2 className="text-2xl font-bold mb-2">Choose data source</h2>
                <p className="text-gray-600 mb-6">
                  Select how you'd like to add items to ExpiTrack.
                </p>
                
                <div className="space-y-4 mb-8">
                  {getDataSourceOptions().map((source) => (
                    <div 
                      key={source}
                      className={`p-4 rounded-xl border-2 transition-all cursor-pointer
                        ${selectedDataSource === source 
                          ? 'border-expitrack-primary bg-expitrack-primary/5' 
                          : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'}`}
                      onClick={() => handleDataSourceSelect(source)}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="font-semibold">{dataSourceLabels[source]}</h3>
                          <p className="text-sm text-gray-600">
                            {source === "manual" && "Add items one by one manually"}
                            {source === "csv" && "Upload a CSV file with your items"}
                            {source === "barcode" && "Scan barcodes to add items quickly"}
                            {source === "pos" && "Connect to your point of sale system"}
                            {source === "batch" && "Upload multiple items in a batch"}
                          </p>
                        </div>
                        <div>
                          {source === "csv" && <Upload className="w-5 h-5 text-gray-400" />}
                          {source === "batch" && <Upload className="w-5 h-5 text-gray-400" />}
                        </div>
                      </div>
                      
                      {/* File upload UI for CSV and Batch options */}
                      {selectedDataSource === source && (source === "csv" || source === "batch") && (
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <button
                            onClick={handleFileSelect}
                            className={`w-full py-3 px-4 border-2 border-dashed rounded-lg text-center transition-colors
                              ${fileSelected 
                                ? 'border-expitrack-primary/50 bg-expitrack-primary/5 text-expitrack-primary' 
                                : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
                          >
                            {fileSelected ? (
                              <div className="flex items-center justify-center">
                                <CheckCircle className="w-5 h-5 mr-2" />
                                <span>File selected</span>
                              </div>
                            ) : (
                              <div className="flex items-center justify-center">
                                <Upload className="w-5 h-5 mr-2" />
                                <span>Click to upload file</span>
                              </div>
                            )}
                          </button>
                          <p className="text-xs text-gray-500 mt-2 text-center">
                            Supported formats: CSV, Excel (.xlsx)
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {step === 3 && (
              <div>
                <h2 className="text-2xl font-bold mb-2">Set alert preferences</h2>
                <p className="text-gray-600 mb-6">
                  Choose when you'd like to be notified about upcoming expirations.
                </p>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Alert window (days before expiration)
                  </label>
                  <div className="flex items-center">
                    <input
                      type="range"
                      min="1"
                      max="30"
                      value={alertWindow}
                      onChange={(e) => setAlertWindow(parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-expitrack-primary"
                    />
                    <span className="ml-4 bg-expitrack-primary text-white rounded-lg px-3 py-1 min-w-[3rem] text-center">
                      {alertWindow}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-2 flex items-center">
                    <Calendar className="w-4 h-4 mr-1 text-expitrack-primary" />
                    You'll receive alerts {alertWindow} days before items expire
                  </p>
                </div>
                
                {isHealthcare && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-start">
                      <div className="flex items-center h-5 mt-0.5">
                        <input
                          id="compliance"
                          type="checkbox"
                          checked={complianceChecked}
                          onChange={() => setComplianceChecked(!complianceChecked)}
                          className="w-4 h-4 border border-gray-300 rounded accent-expitrack-primary focus:ring-2 focus:ring-expitrack-primary/30"
                        />
                      </div>
                      <div className="ml-3">
                        <label htmlFor="compliance" className="font-medium text-gray-700">
                          Regulatory Compliance
                        </label>
                        <p className="text-sm text-gray-500 mt-1">
                          I confirm that my use of ExpiTrack complies with all relevant healthcare regulations, 
                          including HIPAA and other applicable privacy laws.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="p-4 bg-expitrack-primary/10 rounded-lg flex items-start">
                  <AlertCircle className="w-5 h-5 text-expitrack-primary mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700">
                      You can change these settings later in your account preferences.
                      ExpiTrack will use AI to improve predictions based on your usage patterns.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
        
        {/* Navigation buttons */}
        <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
          <button
            onClick={handleBack}
            className="px-4 py-2 flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            disabled={isSubmitting}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            {step === 1 ? 'Back to Home' : 'Back'}
          </button>
          
          <button
            onClick={handleNext}
            className="px-6 py-2 bg-expitrack-primary text-white rounded-full font-medium hover:bg-expitrack-primary/90 transition-all shadow-sm hover:shadow flex items-center"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Processing...' : step === totalSteps ? 'Complete Setup' : 'Continue'}
            {!isSubmitting && <ChevronRight className="w-4 h-4 ml-1" />}
          </button>
        </div>
      </div>
    </PageTransition>
  );
};

export default Onboard;
